import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Popover } from '@headlessui/react'
import { usePopper } from 'react-popper'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getNodeByKey } from 'lexical'
import { ExclamationIcon, FunctionIcon } from '../icons'
import Button from '../Button'
import TextInput from './TextInput'
import { SingleSelect } from './SingleSelect'
import { getHelper, type HelperParam } from './helperRegistry'
import { type HelperArg } from './variableUtils'
import { HelperNode } from './HelperNode'
import { HelperEditorContext } from './HelperEditorContext'
import './HelperNode.css'

interface HelperPillContentProps {
    nodeKey: string
    helperName: string
    args: HelperArg[]
    label: string
}

interface ParamFieldProps {
    param: HelperParam
    value: string
    onChange: (value: string, quoted: boolean) => void
    suggestions: Array<{ path: string, label: string }>
}

const ParamField = ({ param, value, onChange, suggestions }: ParamFieldProps) => {
    if (param.type === 'enum' && param.options) {
        const options = param.required
            ? param.options
            : ['', ...param.options]
        return (
            <SingleSelect
                label={param.label}
                size="small"
                searchable
                value={value || (param.default as string ?? '')}
                onChange={v => onChange(v, true)}
                options={options}
                getOptionDisplay={o => o || '\u2014'}
                placeholder={param.description}
            />
        )
    }

    if (param.type === 'variable') {
        const options = suggestions.map(s => s.path)
        return (
            <SingleSelect
                label={param.label}
                size="small"
                searchable
                value={value}
                onChange={v => onChange(v, false)}
                options={options}
                getOptionDisplay={o => {
                    const match = suggestions.find(s => s.path === o)
                    return match?.label ?? o
                }}
                getOptionSubtitle={o => o}
                getSelectedOptionDisplay={o => {
                    const match = suggestions.find(s => s.path === o)
                    return match?.label ?? o
                }}
                placeholder={param.description ?? 'Select a variable\u2026'}
            />
        )
    }

    return (
        <TextInput
            name={param.name}
            label={param.label}
            type={param.type === 'number' ? 'number' : 'text'}
            size="small"
            value={value}
            placeholder={param.default != null ? String(param.default) : param.description ?? ''}
            onChange={v => onChange(String(v), param.type !== 'variable')}
        />
    )
}

const hasRequiredMissing = (params: HelperParam[], args: HelperArg[]): boolean => {
    return params.some((param, i) => {
        if (!param.required) return false
        const arg = args[i]
        return !arg?.value
    })
}

export default function HelperPillContent({ nodeKey, helperName, args, label }: HelperPillContentProps) {
    const [editor] = useLexicalComposerContext()
    const { suggestions, autoOpenNodeKey, clearAutoOpen } = useContext(HelperEditorContext)
    const definition = getHelper(helperName)
    const params = definition?.params ?? []

    const [referenceElement, setReferenceElement] = useState<Element | null>(null)
    const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
        modifiers: [{ name: 'offset', options: { offset: [0, 4] } }],
    })

    const [localArgs, setLocalArgs] = useState<HelperArg[]>(() => {
        return params.map((param, i) => {
            if (i < args.length) return args[i]
            const defaultVal = param.default != null ? String(param.default) : ''
            return { value: defaultVal, quoted: param.type !== 'variable' }
        })
    })

    useEffect(() => {
        const updated = params.map((param, i) => {
            if (i < args.length) return args[i]
            const defaultVal = param.default != null ? String(param.default) : ''
            return { value: defaultVal, quoted: param.type !== 'variable' }
        })
        setLocalArgs(updated)
    }, [args, params])

    const isInvalid = useMemo(
        () => hasRequiredMissing(params, args),
        [params, args],
    )

    const updateNode = useCallback((newArgs: HelperArg[]) => {
        editor.update(() => {
            const node = $getNodeByKey(nodeKey)
            if (node instanceof HelperNode) {
                node.setArgs(newArgs)
            }
        })
    }, [editor, nodeKey])

    const handleParamChange = useCallback((index: number, value: string, quoted: boolean) => {
        setLocalArgs(prev => {
            const updated = [...prev]
            updated[index] = { value, quoted }
            return updated
        })
    }, [])

    const handleClose = useCallback(() => {
        const trimmed = localArgs.map((arg, i) => {
            if (!arg.value && params[i]?.default != null) {
                return { value: String(params[i].default), quoted: arg.quoted }
            }
            return arg
        })
        updateNode(trimmed)
        clearAutoOpen()
    }, [localArgs, params, updateNode, clearAutoOpen])

    const shouldAutoOpen = autoOpenNodeKey === nodeKey

    useEffect(() => {
        if (shouldAutoOpen && referenceElement) {
            requestAnimationFrame(() => {
                (referenceElement as HTMLElement).click()
                clearAutoOpen()
            })
        }
    }, [shouldAutoOpen, referenceElement, clearAutoOpen])

    const pillClass = isInvalid
        ? 'pill-content helper-pill-content invalid'
        : 'pill-content helper-pill-content'

    return (
        <Popover className="helper-pill-popover-wrapper">
            {({ close }) => (
                <>
                    <Popover.Button
                        as="span"
                        ref={setReferenceElement}
                        className={pillClass}
                    >
                        <FunctionIcon />
                        {label}
                        {isInvalid && <ExclamationIcon />}
                    </Popover.Button>
                    <Popover.Panel
                        ref={setPopperElement}
                        style={styles.popper as any}
                        {...attributes.popper}
                        className="helper-popover"
                    >
                        <div className="helper-popover-header">
                            {definition?.label ?? helperName}
                            {definition?.description && (
                                <span className="helper-popover-desc">{definition.description}</span>
                            )}
                        </div>
                        <div className="helper-popover-fields">
                            {params.map((param, i) => (
                                <ParamField
                                    key={param.name}
                                    param={param}
                                    value={localArgs[i]?.value ?? ''}
                                    onChange={(value, quoted) => handleParamChange(i, value, quoted)}
                                    suggestions={suggestions}
                                />
                            ))}
                        </div>
                        <div className="helper-popover-actions">
                            <Button
                                size="small"
                                onClick={() => {
                                    handleClose()
                                    close()
                                }}
                            >
                                Apply
                            </Button>
                        </div>
                    </Popover.Panel>
                </>
            )}
        </Popover>
    )
}
